import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { InlineTextStyle } from "@/components/doc/right-sidebar/text-style/InlineTextStyle";
import { Sidebar } from "@/components/sidebar";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { PanelToolbar } from "@/components/sidebar/PanelToolbar";
import { twMerge } from "@/utils/twMerge";

import { AlignmentButtons } from "../alignment-buttons/AlignmentButtons";
import { CodeThemeSwitcher } from "../code-style/CodeThemeSwitcher";

import { BlockColorPalette } from "./BlockColorPalette";
import { BlockFormatStyle } from "./BlockFormatStyle";
import { MathIndentationButton } from "./MathIndentationButton";

import type { DocumentModel } from "@/models/DocumentModel";

const Content = observer(function Content({
  doc,
  isCollapsed,
}: {
  doc: DocumentModel;
  isCollapsed?: boolean;
}) {
  const { editorState } = doc.editor;
  const t = useTranslations("sidebar");

  return (
    <>
      <BlockFormatStyle doc={doc} isCollapsed={isCollapsed} />
      <Sidebar.Delimiter
        className={twMerge("my-1.25", isCollapsed && "my-0.5 mx-0 bg-gray-200 dark:bg-gray-800")}
      />
      {!editorState.isExpression && !editorState.isCodeBlock && (
        <>
          <InlineTextStyle doc={doc} isCollapsed={isCollapsed} />
          <Sidebar.Delimiter
            className={twMerge(
              "my-1.25",
              isCollapsed && "my-0.5 mx-0 bg-gray-200 dark:bg-gray-800",
            )}
          />
        </>
      )}
      <PanelToolbar
        label={t("toolbars.layout")}
        isCollapsed={isCollapsed}
        className="flex-1 justify-between"
      >
        {!isCollapsed && editorState.isCode && <CodeThemeSwitcher doc={doc} />}
        <AlignmentButtons doc={doc} isCollapsed={isCollapsed} />
        {!isCollapsed && editorState.isParagraphNode && (
          <MathIndentationButton
            doc={doc}
            className={twMerge("ml-1", editorState.blockBackgroundColor && "mr-auto")}
          />
        )}
        {!isCollapsed && editorState.blockBackgroundColor && <BlockColorPalette doc={doc} />}
      </PanelToolbar>
    </>
  );
});

export const TextStyle = observer(function TextStyle({
  doc,
  isCollapsed,
}: {
  doc: DocumentModel;
  isCollapsed?: boolean;
}) {
  const t = useTranslations("sidebar.blocks");
  const { tiptap } = doc.editor;

  if (!tiptap) {
    return null;
  }

  return isCollapsed ? (
    <div
      role="group"
      aria-label={t("text.title")}
      className="flex flex-col gap-1 rounded-8 p-0.75 bg-gray-100 dark:bg-gray-900"
    >
      <Content doc={doc} isCollapsed />
    </div>
  ) : (
    <ActionsDisclosure
      data-panel="text_style"
      title={t("text.title")}
      disclosureProps={{ defaultOpen: true }}
      disclosurePanelProps={{ className: "-mt-1.25" }}
    >
      <Content doc={doc} />
    </ActionsDisclosure>
  );
});
